import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Plus, Search } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import {
  buildCommunicationsExportUrl,
  type CommunicationChannel,
  type CommunicationLogQuery,
} from "@/api/communications";
import { useCommunications, useRestoreCommunication } from "./hooks/useCommunications";
import {
  INBOX_SCOPE,
  defaultScopeFor,
  normalizeScope,
  type InboxScope,
} from "./inboxScopes";
import { ChannelFilterChips } from "./components/ChannelFilterChips";
import { CommunicationListRow } from "./components/CommunicationListRow";
import { LogCommunicationModal } from "./components/LogCommunicationModal";
import { InboxLeftRail } from "./components/InboxLeftRail";

const PAGE_SIZE = 50;

export function CommunicationsInboxPage() {
  const { hasPermission } = useAuth();
  const canExport = hasPermission("Communications.Export");
  const canViewAll = hasPermission("Communications.ViewAll");

  const [params, setParams] = useSearchParams();
  const channel = (params.get("channel") as CommunicationChannel | null) ?? null;
  const search = params.get("q") ?? "";
  const page = Number(params.get("page") ?? "1") || 1;

  // The RAW value goes to the server, which owns the rule; the normalised one
  // drives the rail's highlight, so a stale `?scope=compliance` in a regular
  // caller's URL lights up the view they are actually being served.
  const rawScope = params.get("scope");
  const scope = normalizeScope(rawScope, canViewAll);
  const defaultScope = defaultScopeFor(canViewAll);

  const [openModal, setOpenModal] = useState(false);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const query: CommunicationLogQuery = useMemo(() => {
    const q: CommunicationLogQuery = { page, pageSize: PAGE_SIZE };
    if (channel) q.channel = channel;
    if (search.trim()) q.q = search.trim();
    if (rawScope) q.scope = rawScope;
    return q;
  }, [page, channel, search, rawScope]);

  const list = useCommunications(query);
  const restore = useRestoreCommunication();

  const totalPages =
    list.data && list.data.pageSize > 0
      ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize))
      : 1;

  // "Nothing here" and "nothing matches what you asked for" are different
  // facts, and only the second is about the filters.
  const isFiltered =
    Boolean(search.trim()) || Boolean(channel) || scope !== defaultScope;

  // "You haven't logged any communications yet" is a claim about a TOTAL, so it
  // is gated on the total and not merely on an empty page. A regular caller
  // stranded on `?page=2` after their own count fell below one page gets an
  // empty `data` with a non-zero `totalCount`; without this conjunct the page
  // told somebody with 45 entries that they had logged none.
  const hasLoggedNothing = list.data?.totalCount === 0;

  const headerDescription = canViewAll
    ? "Central log of every external call, voicemail, email, SMS, and meeting."
    : "Every call, voicemail, email, SMS and meeting you've logged.";

  return (
    <>
      <PageHeader
        eyebrow="Home"
        title="Communications"
        description={headerDescription}
        actions={
          <div className="flex items-center gap-space-2">
            {canExport && (
              <a
                href={buildCommunicationsExportUrl(query)}
                download
                className="inline-flex items-center justify-center gap-space-2 h-10 px-[22px] py-space-2 font-display font-bold uppercase text-label tracking-button rounded-md border-[1.5px] border-gold text-onyx hover:bg-gold-highlight/10"
                aria-label="Export communications as CSV"
              >
                <Download size={14} aria-hidden /> Export CSV
              </a>
            )}
            <Button onClick={() => setOpenModal(true)} type="button">
              <Plus size={16} aria-hidden /> New
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[14rem_1fr] gap-space-4">
        <InboxLeftRail
          scope={scope}
          canViewAll={canViewAll}
          onChange={(next: InboxScope) =>
            updateParam("scope", next === defaultScope ? null : next)
          }
        />

        <div>
          <div className="flex flex-col md:flex-row md:items-end gap-space-3 mb-space-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search
                  size={16}
                  aria-hidden
                  className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
                />
                <Input
                  value={search}
                  onChange={(e) => updateParam("q", e.target.value)}
                  placeholder="Search subject or body"
                  className="pl-space-5"
                  aria-label="Search communications"
                />
              </div>
            </div>
          </div>

          <ChannelFilterChips
            value={channel}
            onChange={(next) =>
              updateParam("channel", next ? next : null)
            }
            className="mb-space-4"
          />

          {list.isLoading && (
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <li
                  key={i}
                  className="h-16 rounded-md bg-white-warm animate-pulse"
                  aria-hidden
                />
              ))}
            </ul>
          )}

          {list.isError && (
            <div className="text-small text-status-error">
              Could not load communications. {(list.error as Error).message}
            </div>
          )}

          {list.data && list.data.data.length === 0 && (
            <div className="rounded-md border border-border-light bg-white-pure p-space-5 text-center">
              <p className="text-small text-gray-mid m-0">
                {!isFiltered && !canViewAll && hasLoggedNothing
                  ? 'You haven\'t logged any communications yet. Click "+ New" to log the first one.'
                  : 'No communications match your filters yet. Click "+ New" to log the first one.'}
              </p>
            </div>
          )}

          {list.data && list.data.data.length > 0 && (
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {list.data.data.map((entry) => (
                <li key={entry.id}>
                  <CommunicationListRow
                    entry={entry}
                    // Restore is offered only in Compliance view — the one view
                    // that returns a deleted entry, and one only a
                    // Communications.ViewAll holder can select, which is the
                    // same permission RestoreAsync requires.
                    onRestore={
                      scope === INBOX_SCOPE.Compliance
                        ? (id) => restore.mutate(id)
                        : undefined
                    }
                    restorePending={restore.isPending}
                  />
                </li>
              ))}
            </ul>
          )}

          {list.data && list.data.totalCount > list.data.pageSize && (
            <div className="flex items-center justify-between mt-space-3">
              <span className="text-label text-gray-mid">
                Page {list.data.page} of {totalPages} · {list.data.totalCount} total
              </span>
              <div className="flex gap-space-2">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={list.data.page <= 1}
                  onClick={() => updateParam("page", String(Math.max(1, page - 1)))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={list.data.page >= totalPages}
                  onClick={() => updateParam("page", String(page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <LogCommunicationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        navigateOnSave
      />
    </>
  );
}
